<script setup lang="ts">
import type { ContactFormModalProps, ContactMethod } from '.';
import type { OtpProvider } from '../LoginForm';
import type { ContactFormSectionExpose } from '~/components/ContactFormSection';
import type { PropertyTypeEnum } from '~/core/entities/CRMEnums';

import {
	useContactFormActions,
	useContactFormEvents,
	useContactFormModal,
	useLeadFlowLogger
} from '~/composables/contactForm';
import { useEvents } from '~/composables/events/useEvents';
import { useContactFormExternalHandler } from '~/composables/useContactFormExternalHandler';
import { extractErrorLogContext } from '~/composables/useLogger';
import {
	mailSouPilar,
	pilarFormattedPhone,
	pilarFormattedPhoneWhatsAppLink
} from '~/constants';
import { propertyTypesTranslations } from '~/core/entities/CRMEnums';
import { EventBusKeys } from '~/core/helpers/symbols';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { cn } from '~/utils';
import { createLocaleTranslator } from '~/utils/localeMessages';
import { slugify } from '~~/utils';

import { ContactFormSection } from '~/components/ContactFormSection';
import OTPForm from '~/components/LoginForm/Steps/ThirdStep/Step.vue';
import { PrivacyTermsStatement } from '~/components/PrivacyTermsStatement';
import { DialogTitle } from '~/components/ui/dialog';
import TextLink from '~/components/ui/text-link/TextLink.vue';

import IconLineMdLoadingTwotoneLoop from '~icons/line-md/loading-twotone-loop';
import NorthEastIcon from '~icons/material-symbols-light/north-east';
import WestIcon from '~icons/material-symbols-light/west';
import IconMaterialSymbolsCallOutline from '~icons/material-symbols/call-outline';
import CheckCircleIcon from '~icons/material-symbols/check-circle';
import DoneIcon from '~icons/material-symbols/done';
import ErrorCircleIcon from '~icons/material-symbols/error';
import IconMaterialSymbolsMailOutline from '~icons/material-symbols/mail-outline';

import propertyPricesLocale from './ContactFormModal.locale.json';

const props = withDefaults(defineProps<ContactFormModalProps>(), {
	context: 'property'
});

const strings = propertyPricesLocale.pt;

const { translate, translateByContext } = createLocaleTranslator(
	strings as Record<string, unknown>,
	props.context
);

const router = useRouter();
const isOpen = defineModel<boolean>();

const emit = defineEmits<{
	'scroll-to-similar-properties': [];
}>();

const modalState = useContactFormModal(props, { t: translate });

const actions = useContactFormActions({
	...modalState,
	props
});

const events = useContactFormEvents({ ...modalState, props });

const logger = useLeadFlowLogger('ContactFormModal');

function handleModalClose(shouldScroll: boolean = false) {
	isOpen.value = false;
	events.addEventsCloseModal();

	if (shouldScroll) {
		nextTick(() => {
			emit('scroll-to-similar-properties');
		});
	}
}

function handleCloseButtonClick(event: Event) {
	const target = event.target as HTMLElement;
	const button = target.closest('button');

	if (button && isOpen.value) {
		const srOnlySpan = button.querySelector('span.sr-only');
		if (srOnlySpan?.textContent?.includes('Close')) {
			event.preventDefault();
			event.stopPropagation();
			const shouldScroll = modalState.currentStep.value === 'success-message';
			handleModalClose(shouldScroll);
		}
	}
}

useEventListener(document, 'click', handleCloseButtonClick, { capture: true });

const handleContactMethodClick = (link: ContactMethod) => {
	events.handleContactMethodClick(link);
};

const wasOpenedByExternalHandler = ref(false);

const { user: nuxtAuthUser } = useUserSession();

useContactFormExternalHandler({
	isOpen: isOpen as Ref<boolean>,
	currentStep: modalState.currentStep,
	user: nuxtAuthUser,
	isUserLoggedIn: modalState.isUserLoggedIn,
	goToOtpFormStep: actions.goToOtpFormStep,
	sendForm: actions.sendForm,
	submitExternalForm: actions.submitExternalForm,
	addEventSuccessMessage: events.addEventSuccessMessage,
	addEventError: events.addEventError
});

onClientEventBus(useEventBus<void>(EventBusKeys.openContactModalAtOtp), () => {
	wasOpenedByExternalHandler.value = true;
});

const contactMethodProps = [
	{
		label: mailSouPilar,
		route: `mailto:${mailSouPilar}`,
		icon: IconMaterialSymbolsMailOutline
	},
	{
		label: pilarFormattedPhone,
		route: pilarFormattedPhoneWhatsAppLink,
		icon: IconMaterialSymbolsCallOutline
	}
];

const contactFormSection = useTemplateRef<ContactFormSectionExpose | null>(
	'contactFormSection'
);
const otpFormRef = useTemplateRef('otpFormRef');

async function handleFormSubmit() {
	try {
		await actions.sendForm();
	} catch (error) {
		const errorContext = {
			context: props.context,
			propertyId: props.propertyId,
			condoId: props.condoId,
			currentStep: modalState.currentStep.value,
			isUserLoggedIn: modalState.isUserLoggedIn.value
		};
		logger.error('Erro não tratado ao submeter formulário de contato', {
			...errorContext,
			...extractErrorLogContext(error)
		});
		logger.rumError(error, { ...errorContext, stage: 'modal_submit' });
		actions.goToErrorMessage((error as Error)?.message);
	}
}

function closeModal() {
	isOpen.value = false;
	events.addEventsCloseModal();
}

function closeModalWithScroll() {
	handleModalClose(true);
}

function goToContactFormStep() {
	actions.goToContactFormStep();
	events.addEventReturnClicked();

	nextTick(async () => {
		if (contactFormSection.value) {
			contactFormSection.value.validateFirstEmptyFieldToFocus();
		}
	});
}

const { trackCtaViewMoreClicked } = useEvents();

const goToSimilarPropertiesOnListing = () => {
	const propertyTypeEnum = Object.entries(propertyTypesTranslations).find(
		([_, name]) => name === props.propertyTypeName
	)?.[0] as PropertyTypeEnum | undefined;

	const newFilters = {
		maxAskingPrice: props.askingPrice ?? undefined,
		propertyTypes: propertyTypeEnum ? [propertyTypeEnum] : undefined,
		locationSlug: slugify(props.locationSlug ?? '')
	};

	const url = PropertyFiltersPresenter.getPathFromFilters(
		newFilters,
		{},
		{
			page: 'imoveis'
		}
	);

	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_FLOW]: 'lead form',
		[MixpanelProps.CURRENT_PAGE]: modalState.currentPageName.value,
		[MixpanelProps.CTA_TEXT]: strings.similarProperties,
		[MixpanelProps.DESTINATION_URL]: url
	});

	router.push(url);
};

function handleFormDataUpdate(
	updatedData: Partial<{
		name: string;
		phone: string;
		email: string;
		userMessage: string;
	}>
) {
	if ('name' in updatedData) {
		const value = updatedData.name?.trim() ?? '';
		modalState.setFieldValue('name', value);
	}
	if ('phone' in updatedData) {
		const value = updatedData.phone?.trim() ?? '';
		modalState.setFieldValue('phone', value);
	}
	if ('email' in updatedData) {
		const value = updatedData.email?.trim() ?? '';
		modalState.setFieldValue('email', value);
	}
	if ('userMessage' in updatedData) {
		const value = updatedData.userMessage?.trim() ?? '';
		modalState.setFieldValue('userMessage', value);
	}
}

const modalTitle = computed<string>(() => {
	const i18nOptions = { brokerName: modalState.brokerFirstName.value };

	if (modalState.currentStep.value === 'success-message') {
		return strings.successTitle;
	}

	if (modalState.currentStep.value === 'submit-error-message') {
		return strings.errorTitle;
	}

	if (modalState.currentStep.value === 'otp-form') {
		return strings.phoneOtpTitle;
	}

	return modalState.isUserLoggedIn.value
		? translateByContext('titleForLoggedUser', i18nOptions)
		: translateByContext('titleForUnloggedUser', i18nOptions);
});

const getImageSrc = useImage();
const brokerSrc = computed(() =>
	getImageSrc(props.broker?.profilePicture?.croppedUrl || '', {
		format: 'webp',
		width: 128
	})
);

const validateFirstEmptyFieldToFocus = async () => {
	await nextTick();
	if (contactFormSection.value) {
		await contactFormSection.value.validateFirstEmptyFieldToFocus();
	}
};

whenever(isOpen, (newValue, oldValue) => {
	if (
		!newValue &&
		oldValue &&
		(modalState.currentStep.value === 'success-message' ||
			modalState.currentStep.value === 'submit-error-message')
	) {
		actions.goToContactFormStep();
	}
});

watch(
	modalState.initialUserMessage,
	message => modalState.setFieldValue('userMessage', message),
	{
		immediate: true
	}
);

watch(
	[() => isOpen.value, () => modalState.user.value],
	async () => {
		if (!isOpen.value || !modalState.user.value) return;
		const userData = modalState.user.value;

		if (!userData) return;

		if (!modalState.name.value)
			modalState.setFieldValue('name', userData.name ?? '');
		if (!modalState.phone.value)
			modalState.setFieldValue('phone', modalState.getUserFulfilledPhone());
		if (!modalState.email.value)
			modalState.setFieldValue('email', userData.email ?? '');
		if (!modalState.userMessage.value) {
			modalState.setFieldValue(
				'userMessage',
				modalState.initialUserMessage.value
			);
		}

		await nextTick();
		validateFirstEmptyFieldToFocus();

		// Trigger form validation after autofill
		await nextTick();
		await modalState.validate();
	},
	{ immediate: true }
);

watch(isOpen, async newValue => {
	if (newValue) {
		await nextTick();

		if (
			(modalState.currentStep.value === 'success-message' ||
				modalState.currentStep.value === 'submit-error-message') &&
			!wasOpenedByExternalHandler.value
		) {
			actions.goToContactFormStep();
		}

		wasOpenedByExternalHandler.value = false;

		const shouldRespectDefaultExpanded = props.defaultExpanded === false;

		const isExpandableContainerExpanded =
			!shouldRespectDefaultExpanded &&
			(modalState.isUserLoggedIn.value ||
				modalState.shouldExpandContainer.value);

		if (isExpandableContainerExpanded && contactFormSection.value) {
			contactFormSection.value.expandContainer();
			await nextTick();
		}

		validateFirstEmptyFieldToFocus();
		events.addEventContactFormOpened();
	}
});

const handleToggleChange = (isExpanded: boolean) => {};

const otpProvider = ref<OtpProvider>('whatsapp');

const otpProviderName = computed(
	() => strings.otpProviderName[otpProvider.value]
);

const otpDescriptionParts = computed(() => {
	const [before = '', after = ''] =
		strings.phoneOtpDescription[otpProvider.value].split('{0}');
	return { before, after };
});

const otpFallbackParts = computed(() => {
	const [before = '', after = ''] =
		strings.phoneOtpFallbackMessage.split('{0}');
	return { before, after };
});
const isContactFormStep = computed(
	() => modalState.currentStep.value === 'contact-form'
);
const isOtpFormStep = computed(
	() => modalState.currentStep.value === 'otp-form'
);
const isSuccessMessageStep = computed(
	() => modalState.currentStep.value === 'success-message'
);
const isErrorMessageStep = computed(
	() => modalState.currentStep.value === 'submit-error-message'
);

const isFinalState = computed(
	() => isSuccessMessageStep.value || isErrorMessageStep.value
);

const showCloseIcon = computed(() => !isOtpFormStep.value);

const modalContentClass = computed(() =>
	cn(
		'z-[301] border-none w-[90%] md:w-[37.5rem] bg-white text-primary overflow-y-auto no-scrollbar max-h-[95%]',
		{
			'md:max-w-[37.5rem]': !isOtpFormStep.value,
			'max-w-[420px]': isOtpFormStep.value
		}
	)
);

const footerClass = computed(() => [
	'bg-white border-t border-white-dark-3 sticky bottom-0',
	{
		'p-4 md:px-5': isSuccessMessageStep.value,
		'px-4 py-4 md:py-2 flex w-full md:justify-end justify-center':
			isContactFormStep.value || isOtpFormStep.value
	}
]);

const successIconBgClass = computed(() => ({
	'bg-success-light-1': isSuccessMessageStep.value,
	'bg-error-light-1': isErrorMessageStep.value
}));

defineExpose({
	goToOtpFormStep: actions.goToOtpFormStep
});
</script>

<template>
	<ModalPortalModal
		v-model:open="isOpen"
		:show-close-icon="showCloseIcon"
		:prevent-interact-outside="false"
		:content-class="modalContentClass"
		overlay-class="z-[300] bg-black/40"
		header-class="font-matter-sq text-xl md:text-2xl font-normal text-left text-primary p-5 h-[4.5rem] md:h-[4.75rem]"
		:footer-class="footerClass"
	>
		<template #header>
			<div v-if="isOtpFormStep">
				<DialogTitle
					class="flex items-center gap-4 font-matter-sq text-xl font-normal leading-normal tracking-[-0.03rem] text-primary md:gap-6 md:text-2xl"
				>
					<WestIcon class="cursor-pointer" @click="goToContactFormStep" />
					{{ strings.phoneOtpTitle }}
				</DialogTitle>
			</div>
			<DialogTitle
				v-else
				class="font-matter-sq text-xl font-normal leading-normal tracking-[-0.03rem] lg:text-2xl"
			>
				{{ modalTitle }}
			</DialogTitle>
		</template>

		<template #default>
			<DialogDescription class="hidden" />

			<template v-if="isContactFormStep">
				<ContactFormSection
					ref="contactFormSection"
					class="px-5 pb-6 pt-5 md:px-6"
					:form-data="{
						name: modalState.name.value,
						phone: modalState.phone.value,
						email: modalState.email.value,
						userMessage: modalState.userMessage.value
					}"
					:validation="{
						errors: modalState.errors.value,
						isValid: modalState.meta.value.valid,
						isPhoneDisabled: !!modalState.isPhoneDisabled.value
					}"
					:form-attributes="{
						nameAttrs: modalState.nameAttrs,
						phoneAttrs: modalState.phoneAttrs,
						emailAttrs: modalState.emailAttrs,
						userMessageAttrs: modalState.userMessageAttrs
					}"
					:broker-info="{
						broker: props.broker,
						name: modalState.brokerName.value,
						avatarSrc: brokerSrc,
						message: translateByContext('brokerMessage'),
						atText: translateByContext('brokerAt', {
							brokerCompany: props.companyName
						}),
						companyName: props.companyName,
						companyLogo: props.companyLogo
					}"
					:text-content="{
						expandText: strings.showMoreInfo,
						collapseText: strings.showLessInfo,
						namePlaceholder: translateByContext('form.name.placeholder'),
						emailPlaceholder: translateByContext('form.email.placeholder'),
						userMessagePlaceholder: translateByContext(
							'form.userMessage.placeholder'
						)
					}"
					:app-state="{
						isPropertyContext: modalState.isPropertyContext.value,
						isUserLoggedIn: modalState.isUserLoggedIn.value,
						shouldExpandContainer: modalState.shouldExpandContainer.value
					}"
					:analytics-data="{
						propertyCommercialId: props.propertyCommercialId,
						askingPrice: props.askingPrice ?? null,
						videoShorts: props.videoShorts
					}"
					:default-expanded="props.defaultExpanded"
					@update:form-data="handleFormDataUpdate"
					@toggle-expanded="handleToggleChange"
					@submit="handleFormSubmit"
				/>
			</template>

			<div v-else-if="isOtpFormStep" class="px-7 py-5 lg:py-6">
				<div
					class="mb-6 flex flex-col gap-1 text-base font-normal leading-[150%]"
				>
					<p>
						<span>{{ otpDescriptionParts.before }}</span
						><strong class="font-medium">{{ otpProviderName }}</strong
						><span>{{ otpDescriptionParts.after }}</span>
					</p>

					<p
						v-if="otpProvider === 'whatsapp'"
						class="text-sm text-primary-light-3"
					>
						<span>{{ otpFallbackParts.before }}</span
						><strong class="font-medium">SMS</strong
						><span>{{ otpFallbackParts.after }}</span>
					</p>
				</div>

				<OTPForm
					ref="otpFormRef"
					v-model:is-o-t-p-valid="modalState.isOTPValid.value"
					:phone="modalState.phone.value"
					:is-submitting="modalState.isSubmittingOtpForm.value"
					:show-confirm-button="false"
					is-active
					start-countdown-immediately
					@validated="actions.handleOtpFormValidation"
					@change-number="goToContactFormStep"
					@resend="actions.resendOtp"
				/>
			</div>

			<div
				v-else-if="isFinalState"
				:data-testid="`${modalState.currentStep.value}-step`"
			>
				<div class="flex flex-col items-center gap-4 px-5 py-8">
					<div
						class="flex size-15 items-center justify-center rounded-full"
						:class="successIconBgClass"
					>
						<CheckCircleIcon
							v-if="isSuccessMessageStep"
							class="size-13 text-success"
						/>
						<ErrorCircleIcon v-else class="size-13 text-error" />
					</div>

					<div class="flex flex-col items-center justify-center gap-3">
						<strong
							class="text-center font-matter-sq text-lg font-normal leading-normal tracking-[-0.0225rem]"
							:data-test-id="isSuccessMessageStep ? 'pdp-success-message' : ''"
						>
							<template v-if="isSuccessMessageStep">
								{{ strings.successConfirmationMessage }}
							</template>
							<template v-else>
								{{ strings.formSubmitErrorMessage }}
							</template>
						</strong>

						<p class="text-center font-sans text-sm font-normal leading-normal">
							<template v-if="isSuccessMessageStep">
								{{
									translateByContext(
										'successConfirmationComplementaryMessage',
										{
											brokerName: modalState.brokerFirstName.value
										}
									)
								}}
							</template>
							<template v-else>
								{{ strings.formSubmitErrorComplementaryMessage }}

								<div
									class="flex w-full flex-col items-center justify-center gap-5 pt-5 md:flex-row"
								>
									<NuxtLink
										v-for="link in contactMethodProps"
										:key="link.label"
										:to="link.route"
										target="_blank"
										@click="handleContactMethodClick(link)"
									>
										<TextLink
											as="p"
											class="flex flex-row items-center justify-center gap-2 text-sm font-normal leading-[150%] text-primary underline decoration-black/20 underline-offset-8"
										>
											<Component :is="link.icon" />
											{{ link.label }}
										</TextLink>
									</NuxtLink>
								</div>
							</template>
						</p>
					</div>
				</div>
			</div>

			<div
				v-if="isContactFormStep"
				class="bg-white py-6 pl-5 pt-1 text-left text-xs font-normal leading-4.5 text-primary-light-1"
			>
				<PrivacyTermsStatement
					text-class="flex flex-wrap gap-1"
					link-class="underline decoration-primary-light-1 underline-offset-[0.2rem]"
					@terms-click="events.addEventsTermsOrPolicy('terms')"
					@privacy-click="events.addEventsTermsOrPolicy('policy')"
				/>
			</div>
		</template>

		<template #footer>
			<div v-if="isContactFormStep" class="w-full md:w-auto">
				<Button
					size="md"
					class="w-full text-base font-medium"
					:disabled="
						!modalState.meta.value.valid || modalState.isSubmittingForm.value
					"
					data-test-id="pdp-send-contact-form-button"
					@click="handleFormSubmit"
				>
					<IconLineMdLoadingTwotoneLoop
						v-if="modalState.isSubmittingForm.value"
						class="size-[1.7rem]"
					/>
					<template v-else>
						{{ translateByContext('form.sendButtonText') }}
						<NorthEastIcon class="size-5 text-beige" />
					</template>
				</Button>
			</div>

			<div v-else-if="isOtpFormStep" class="w-full">
				<Button
					:disabled="modalState.isSubmittingOtpForm.value"
					class="h-12 w-full text-base font-medium"
					data-testid="submit-confirmation-button"
					@click="otpFormRef?.submitCode"
				>
					<IconLineMdLoadingTwotoneLoop
						v-if="modalState.isSubmittingOtpForm.value"
						class="size-[1.7rem]"
					/>

					<template v-else>
						{{ strings.confirmButton }}
					</template>
				</Button>
			</div>

			<div
				v-else-if="isSuccessMessageStep"
				class="flex w-full flex-col gap-3 lg:flex-row lg:justify-end"
			>
				<Button
					v-if="props.locationSlug"
					size="md"
					class="ml-auto flex gap-2 text-base font-medium md:gap-3 lg:w-fit"
					variant="secondary"
					@click="goToSimilarPropertiesOnListing"
				>
					{{ strings.similarProperties }}
				</Button>
				<Button
					size="md"
					class="ml-auto flex gap-2 text-base font-medium md:gap-3"
					@click="closeModalWithScroll"
				>
					<DoneIcon class="size-5 text-beige" />
					{{ strings.successConfirmationButtonText }}
				</Button>
			</div>

			<Button
				v-else-if="isErrorMessageStep"
				size="md"
				class="ml-auto flex gap-2 text-base font-medium md:w-fit md:gap-3"
				@click="goToContactFormStep"
			>
				{{ strings.retryButtonText }}
			</Button>
		</template>
	</ModalPortalModal>
</template>
